import React, { useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";
import { Link, useNavigate } from "react-router-dom";
import { Input } from "../components/Input";
import Label from "../components/Label";
import { useReduxDispatch, useReduxSelector } from "../redux/hooks";
import { UserAPI } from "../middleware";
import { Alert } from "@mui/material";
import { LoadingModal } from "../components/Modal";
import { DatePicker, DayCalendarSkeleton, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from 'dayjs';
import {
  SignupFailed,
  SignupPending,
  SignupSuccessful,
} from "../redux/slice/SignupSlice";

type Inputs = {
  patientNumber: string;
  dateOfBirth: string;
};

type ErrorItem = {
  message: string;
  field?: string;
};

const SignUp = () => {
  const { isSignedIn } = useReduxSelector((state) => state.user);
  const { isLoading } = useReduxSelector((state) => state.signup);

  const [open, setOpen] = useState(false);
  const [error, setError] = useState<ErrorItem[]>([]);
  const [selectedDate, setSelectedDate] = useState(dayjs());

  const navigate = useNavigate();
  const dispatch = useReduxDispatch();
  const handleClose = () => setOpen(false);

  React.useEffect(() => {
    isSignedIn && window.location.assign("/find-a-doctor");
  }, [isSignedIn, navigate]);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<Inputs>();

  const onSubmit: SubmitHandler<Inputs> = async (data) => {
    setOpen(true);
    dispatch(SignupPending());
    let { patientNumber } = data;
    let username = patientNumber;  // Set username to patientNumber

    const formattedDateOfBirth = selectedDate.format('YYYY-MM-DD');

    let payload = {
      username,
      patientNumber,
      dateOfBirth: formattedDateOfBirth,
      appClientToken: process.env.REACT_APP_CLIENT_CODE,
      signUpType: "existingPatient",
      address: null,
      contacts: null,
      firstName: null,
      lastName: null,
      otherName: null,
      gender: null,
    };

    console.log("PAYLOAD", payload);

    try {
      const signUpResponse = await UserAPI.signUpExistingPatient(payload);
      console.log(
        "signUpResponse",
        signUpResponse.data
      );

      if (signUpResponse.status === 200) {
        dispatch(SignupSuccessful(signUpResponse));
        setOpen(false);
        navigate("/select-otp-phone-number", {
          state: {
            signUpResponseData: signUpResponse.data
          }
        });
      } else {
        throw new Error('Failed to sign up');
      }
    } catch (error: any) {
      const errorMessage: ErrorItem[] = error?.response?.data?.errors || [{ message: "Unknown error" }];
      dispatch(SignupFailed(errorMessage));
      setOpen(false);
      setError(errorMessage);
    }
  };

  return (
    <>
      {!isSignedIn ? (
        <div className="grid overflow-x-hidden relative grid-cols-1 gap-6 items-center px-8 py-8 h-screen md:py-0 md:px-14 md:grid-cols-2">
          {/* form */}
          <div className="flex z-10 flex-col justify-center">
            <div className="flex justify-center items-center">
              <img
                src="/assets/trust-hospital-logo.png"
                alt="hospital logo"
                className="w-72 cursor-pointer"
                onClick={() => navigate("/")}
              />
            </div>
            <div className="flex flex-col justify-center px-6 py-6 mt-6 rounded-xl border shadow-sm md:px-10 border-tertiary-200">
              <h1 className="text-4xl font-medium text-center">Sign Up</h1>
              {error.length > 0 &&
                error.map((item, index) => (
                  <div key={index}>
                    <Alert severity="error" className="mt-2">
                      {item.message}
                    </Alert>
                  </div>
                ))}
              <form
                onSubmit={handleSubmit(onSubmit)}
                className="flex flex-col mt-6 w-full"
              >
                <div className="mt-4">
                  <Label label="Patient Number" />
                  <div className="mt-3 rounded-3xl">
                    <Input
                      type="text"
                      className="rounded-[26px]"
                      id="patientNumber"
                      {...register("patientNumber", { required: true })}
                      aria-invalid={errors.patientNumber ? "true" : "false"}
                    />
                  </div>
                  {errors.patientNumber && (
                    <p className="mt-1 text-xs text-red-500">
                      *Your patient number is required
                    </p>
                  )}
                </div>

                <div className="mt-4">
                  <Label label="Date of Birth" />
                  <div className="mt-3 w-full">
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <div className="">
                        <DatePicker
                          slotProps={{
                            textField: {
                              sx: {
                                '> .MuiOutlinedInput-root': {
                                  height: 45,
                                  borderRadius: '12px',
                                }
                              }
                            }
                          }}
                          className="p-4 w-full border-none shadow-none"
                          disablePast={false}
                          disableFuture={true}
                          format="DD/MM/YYYY"
                          showDaysOutsideCurrentMonth
                          fixedWeekNumber={5}
                          renderLoading={() => <DayCalendarSkeleton />}
                          value={selectedDate}
                          onChange={(date: any) => setSelectedDate(date)}
                        />
                      </div>
                    </LocalizationProvider>
                  </div>
                  {errors.dateOfBirth && (
                    <p className="mt-1 text-xs text-red-500">
                      *Your date of birth is required
                    </p>
                  )}
                </div>

                <button className="mt-6 w-full h-10 font-medium text-white rounded-2xl bg-primary-50 text-md">
                  Sign Up
                </button>
              </form>

            </div>
            <div className="flex flex-col justify-center px-6 py-6 mt-6 rounded-xl border shadow-sm md:px-10 border-tertiary-200">
              <p className="text-lg text-center">
                If you do not have your patient number, kindly call <br /> us on{" "}
                <a href="tel:+233302761975">
                  <span className="underline text-primary-50">
                    +233 302 761 975{" "}
                  </span>
                </a>{" "}
                before you proceed
              </p>
            </div>
            <div>
              <p className="my-4 text-lg text-center">
                Already Have an Account?{" "}
                <span className="text-primary-50">
                  <Link to="/signin">Sign In</Link>
                </span>
              </p>
            </div>
          </div>
          {/* image */}
          <div className="w-full h-full">
            <img
              src={"/assets/newuser-signup.png"}
              alt="sign up illustration"
              className="object-contain absolute left-60 w-full h-full"
            />
          </div>
          <LoadingModal
            open={open}
            handleClose={handleClose}
            isLoading={isLoading}
          />
        </div>
      ) : (
        <></>
      )}
    </>
  );
};

export default SignUp;
